import React, { useEffect, useContext, useState } from "react";
import { mdiCalculator, mdiClose, mdiContentSave, mdiDownload, mdiFileDocumentOutline } from "@mdi/js";
import intl from "react-intl-universal";
import styled from "styled-components";
import { useLocation } from "wouter";

import entryRest from "../../../rest/entryRest";
import calculationRest from "../../../rest/calculationRest";

import { useSaveEntry } from "./useSaveEntry";
import { EntryContext } from "../../contexts/EntryContext";

import { PlanTab } from "./plan/PlanTab";
import { IxusTab } from "./ixus/IxusTab";
import { RadiosTab } from "./radios/RadiosTab";
import { Spacer } from "../../components/Spacer";
import { Button } from "../../components/Button";
import { DownloadDialog } from "./DownloadDialog";
import { ReportDialog } from "./report/ReportDialog";
import { Tab, Tabs } from "../../components/Tabs";
import { OverviewDialog } from "../entries/OverviewDialog";
import { ContentBody } from "../../containers/ContentBody";
import { WorstcaseDialog } from "../entries/WorstcaseDialog";
import { Dropdown, Option } from "../../components/Dropdown";
import { ContentHeader } from "../../containers/ContentHeader";
import { WattwaechterDialog } from "../entries/WattwaechterDialog";
import { ReportDialog as IxusReportDialog } from "./ixusReport/IxusReportDialog";

const UpperLayerStyle = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: rgba(255, 255, 255, 0.6);
`;

const ProgressBoxStyle = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
    padding: 40px 70px;
    background-color: ${props => props.theme.white};
    border-radius: 8px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
`;

const ProgressTextStyle = styled.p`
    margin: 0;
    font-size: 20px;
    color: ${props => props.theme.black};
`;

const LowerLayerStyle = styled.div`
    position: relative;
`;

export const EntryTabs = props => {
    const { entryId, urlParam1, urlParam2 } = props;

    const entryContext = useContext(EntryContext);

    const [showDownloadWWjson, setShowDownloadWWjson] = useState(false);
    const [showOverviewDialog, setShowOverviewDialog] = useState(false);
    const [showWorstcaseDialog, setShowWorstcaseDialog] = useState(false);
    const [showReportDialog, setShowReportDialog] = useState(false);
    const [showDownloadWWmobil, setShowDownloadWWmobil] = useState(false);
    const [showWattwaechterDialog, setShowWattwaechterDialog] = useState(false);
    const [showDownloadAustriaExcel, setShowDownloadAustriaExcel] = useState(false);
    const [showIxusReportDialog, setShowIxusReportDialog] = useState(false);

    const { entry } = entryContext;
    const [, setLocation] = useLocation();
    const tabMap = new Map([
        [0, "radios"],
        [1, "plan"],
        [2, "ixus"],
    ]);

    const calculating =
        entry?.calcSetting?.calcAreas?.some(calcArea =>
            calcArea.calculations?.some(calculation => !calculation.resultAvailable)
        ) || entry?.ixusSetting?.ixusAreas?.some(ixusArea =>
            ixusArea.calculation?.resultAvailable === false
        ) || false;

    const onClose = () => setLocation("/entries");

    const onChangeTab = tabIndex => setLocation("/entry/" + entry.id + "/" + tabMap.get(tabIndex));

    const downloadEntry = async () => {
        try {
            const entry = await entryRest.getById(entryId);
            if(entry) {
                entryContext.setEntry(await entryRest.getById(entryId));
                return;
            }
        } catch {
            console.error("Couldn't load entry " + entryId);
        }
        onClose();
    };

    useEffect(() => {
        if (entryId === "null") {
            entryContext.emptyEntry();
        } else {
            downloadEntry();
        }
    }, []);

    const activeTab = () => {
        for (const [key, value] of tabMap.entries()) {
            if (value === urlParam1) {
                return key;
            }
        }
        return 0;
    };

    const {saving, saveEntry} = useSaveEntry();

    const onSave = async (entry, action) => {
        await saveEntry(entry);
        action?.();
    };

    const onDownloadWwjson = async () => {
        await onSave(entry, () => setShowDownloadWWjson(true));
    };

    const onDownloadWwmobil = async () => {
        await onSave(entry, () => setShowDownloadWWmobil(true));
    };

    const onDownloadAustriaExcel = async () => {
        await onSave(entry, () => setShowDownloadAustriaExcel(true));
    };

    const onCalculateOverview = () => {
        setShowOverviewDialog(true);
    };

    const onCalculateIxus = async () => {
        await onSave(entry, async () => {
            const newEntry = await calculationRest.ixus(entry.id);
            entryContext.setEntry(newEntry);
        });
    };

    const onCalculateWattwaechter = async () => {
        await onSave(entry, () => setShowWattwaechterDialog(true));
    };

    const onCalculateWorstcase = async () => {
        await onSave(entry, () => setShowWorstcaseDialog(true));
    };

    return (
        <>
            {entry !== undefined &&
                <>
                    <ContentHeader title={entry.name}>
                        <Dropdown
                            svgIcon={mdiCalculator}
                            value={intl.get("entry.calculate")}
                            onButtonClick={onCalculateWattwaechter}
                            disabled={calculating || saving}>
                            <Option
                                onClick={onCalculateWattwaechter}
                                disabled={calculating || saving}>
                                {intl.get("entry.calculateOptions.wattwaechter")}
                            </Option>
                            <Option
                                onClick={onCalculateWorstcase}
                                disabled={calculating || saving}>
                                {intl.get("entry.calculateOptions.worstcase")}
                            </Option>
                            <Option
                                onClick={onCalculateOverview}
                                disabled={calculating || saving}>
                                {intl.get("entry.overview")}
                            </Option>
                            <Option
                                onClick={onCalculateIxus}
                                featureFor="dfmg"
                                disabled={calculating || saving}>
                                {intl.get("entry.calculateOptions.ixus")}
                            </Option>
                        </Dropdown>
                        <Spacer variant="vertical" />
                        <Dropdown
                            svgIcon={mdiDownload}
                            value={intl.get("common.download")}
                            onButtonClick={onDownloadWwmobil}
                            disabled={calculating || saving}>
                            <Option
                                onClick={onDownloadWwjson}
                                disabled={calculating || saving}>
                                {intl.get("entry.downloadOptions.wwjson")}
                            </Option>
                            <Option
                                onClick={onDownloadWwmobil}
                                disabled={calculating || saving}>
                                {intl.get("entry.downloadOptions.wwmobil")}
                            </Option>
                            <Option
                                onClick={onDownloadAustriaExcel}
                                featureFor="towers-at"
                                disabled={calculating || saving}>
                                {intl.get("entry.downloadOptions.austriaExcel")}
                            </Option>
                        </Dropdown>
                        <Spacer variant="vertical" />
                        <Dropdown
                            svgIcon={mdiFileDocumentOutline}
                            value={intl.get("entry.report.reportButton")}
                            onButtonClick={() => setShowReportDialog(true)}
                            disabled={calculating || saving}>
                            <Option
                                onClick={() => setShowReportDialog(true)}
                                disabled={calculating || saving}>
                                {intl.get("entry.report.reportButton")}
                            </Option>
                            <Option
                                onClick={() => setShowIxusReportDialog(true)}
                                featureFor="dfmg"
                                disabled={calculating || saving}>
                                {intl.get("entry.downloadOptions.ixusReport")}
                            </Option>
                        </Dropdown>
                        <Spacer variant="vertical" />
                        <Button
                            svgIcon={mdiContentSave}
                            onClick={() => onSave(entry)}
                            loading={calculating || saving}
                            value={intl.get("common.save")} />
                        <Spacer variant="vertical" />
                        <Button
                            svgIcon={mdiClose}
                            onClick={onClose}
                            disabled={saving}
                            value={intl.get("common.quit")} />
                    </ContentHeader>
                    <ContentBody>
                        {calculating &&
                            <UpperLayerStyle>
                                <ProgressBoxStyle>
                                    <ProgressTextStyle>{intl.get("entry.progress")} {(entry.progress ?? 0) }%</ProgressTextStyle>
                                    <Button
                                        svgIcon={mdiClose}
                                        onClick={async () => entryContext.setEntry(await calculationRest.cancelEntry(entry.id))}
                                        value={intl.get("entry.calcelCalculation")} />
                                </ProgressBoxStyle>
                            </UpperLayerStyle>
                        }
                        <LowerLayerStyle>
                            <Tabs
                                activeTab={activeTab()}
                                onChangeTab={onChangeTab}>
                                <Tab title={intl.get("entry.tabs.radios")}>
                                    <RadiosTab />
                                </Tab>
                                <Tab title={intl.get("entry.tabs.plan")}>
                                    <PlanTab param2={urlParam1 === "plan" ? urlParam2 : undefined} />
                                </Tab>
                                <Tab title={intl.get("entry.tabs.ixus")}
                                    featureFor="dfmg">
                                    <IxusTab param2={urlParam1 === "ixus" ? urlParam2 : undefined} />
                                </Tab>
                            </Tabs>
                        </LowerLayerStyle>
                    </ContentBody>
                    {showDownloadWWjson &&
                        <DownloadDialog
                            type="wwjson"
                            onClose={() => setShowDownloadWWjson(false)} />
                    }
                    {showDownloadWWmobil &&
                        <DownloadDialog
                            type="wwmobil"
                            onClose={() => setShowDownloadWWmobil(false)} />
                    }
                    {showDownloadAustriaExcel &&
                        <DownloadDialog
                            type="austriaExcel"
                            onClose={() => setShowDownloadAustriaExcel(false)} />
                    }
                    {showOverviewDialog &&
                        <OverviewDialog
                            saveEntry={saveEntry}
                            onClose={() => setShowOverviewDialog(false)} />
                    }
                    {showWattwaechterDialog &&
                        <WattwaechterDialog
                            saveEntry={saveEntry}
                            onClose={() => setShowWattwaechterDialog(false)} />
                    }
                    {showWorstcaseDialog &&
                        <WorstcaseDialog
                            saveEntry={saveEntry}
                            onClose={() => setShowWorstcaseDialog(false)} />
                    }
                    {showReportDialog &&
                        <ReportDialog
                            onClose={() => setShowReportDialog(false)} />
                    }
                    {showIxusReportDialog &&
                        <IxusReportDialog
                            onClose={() => setShowIxusReportDialog(false)} />
                    }
                </>
            }
        </>
    );
};